import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import toast, { Toaster } from "react-hot-toast";
import { format } from "date-fns";
import Seo from "components/Seo";
import CustomerLayout from "./customerLayout";
import Button from "../../-components/common/Button";
import SelectBox from "../../-components/common/SelectBox";
import DayPickerInput from "../../-components/common/DayPickerInput";
import { BillListRequest, BillListResponse, usePostBillList } from "hooks/morning/usePostBillList";
import Seal from "assets/morning/seal.png";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { ReactComponent as PageBefore } from "assets/morning/icon/pageBefore.svg";
import { ReactComponent as PageFirst } from "assets/morning/icon/pageFirst.svg";
import { useGetDepartments } from "../../../../../hooks/morning/useGetDept";
import { useMediaQuery } from "hooks/utils/useMediaQuery";
import { useMorningAuthStore } from "../../../../../store/useMorningAuthStore";
import NoLogin from "../../../common/-pages/service/NoLogin";
import { usePostCustomerBillExcel } from "../../../../../hooks/morning/usePostCustomerBillExcel";
import axiosInstance from "hooks/morning/axiosInstanse";
import { enum_api_uri } from "hooks/utils/enum";
import { toastErrorSyle } from "hooks/utils/util";
import { ReactComponent as Notice } from "assets/morning/icon/notice.svg";
import { usePostScanImg } from "hooks/morning/usePostScanImg";
import Spinner from "components/Spinner";
import { usePostTrackingPopPrint } from "hooks/morning/usePostTrackingPopPrint";
import PrintA4Preview from "../../-components/PrintA4Preview";
import { usePostTrackingPopPrintView } from "hooks/morning/usePostTrackingPopPrintView";
import { ReactComponent as Memo } from "assets/morning/icon/memo.svg";
import { ReactComponent as MemoOn } from "assets/morning/icon/memoOn.svg";

const MorningInvoices = () => {
    const accessToken = useMorningAuthStore(state => state.accessToken ?? "");
    const c_company = useMorningAuthStore(state => state.c_company);
    const c_code = useMorningAuthStore(state => state.c_code);
    const c_month_yn = useMorningAuthStore(state => state.c_month_yn);

    const today = new Date();
    const currentYear = today.getFullYear().toString();
    const currentMonth = String(today.getMonth() + 1).padStart(2, "0");

    const [flag, setFlag] = useState<string>("day");
    const [year, setYear] = useState<string>(currentYear);
    const [month, setMonth] = useState<string>(currentMonth);
    const [startDate, setStartDate] = useState<Date | undefined>(today);
    const [endDate, setEndDate] = useState<Date | undefined>(today);
    const [page, setPage] = useState(1);
    const [dept, setDept] = useState("all");
    const [code, setCode] = useState("all");
    const [scanImg, setScanImg] = useState<{ url: string; timestamp: number } | null>(null);
    const responsive1200 = useMediaQuery(1200);

    const [printData, setPrintData] = useState<BillListResponse | null>(null);
    const [printA4Data, setPrintA4Data] = useState<any>(null);
    const [printA4Pop, setPrintA4Pop] = useState<boolean>(false);
    const hasPrintedRef = useRef(false);
    const [requestData, setRequestData] = useState<BillListRequest>({
        getPage: "1",
        getPageSize: "20",
        flag: "day",
        yyyy: currentYear,
        mm: currentMonth,
        startDate: format(today, "yyyy-MM-dd"),
        endDate: format(today, "yyyy-MM-dd"),
        s_p_dept: "",
        et_code: "",
    });

    const { data: billData } = usePostBillList(requestData);
    const { data: deptData } = useGetDepartments();
    const { getExcel } = usePostCustomerBillExcel();
    const { mutate: postScanImg, isPending: isPostScanImgPending } = usePostScanImg();
    const { mutate: postTrackingPopPrint, isPending: isPostTrackingPopPrintPending } = usePostTrackingPopPrint();
    const { mutate: postTrackingPopPrintView, isPending: isPostTrackingPopPrintViewPending } = usePostTrackingPopPrintView();

    const maxPage = billData?.last_page ?? 1;
    const pageSize = 5;
    const currentGroup = Math.floor((page - 1) / pageSize);
    const startPage = currentGroup * pageSize + 1;
    const endPage = Math.min(startPage + pageSize - 1, maxPage);

    useEffect(() => {
        if (responsive1200) {
            setFlag("month");

            const selectedYear = startDate?.getFullYear()?.toString();
            const selectedMonth = startDate ? String(startDate.getMonth() + 1).padStart(2, "0") : undefined;

            setYear(selectedYear ?? "");
            setMonth(selectedMonth ?? "");
        }
    }, [responsive1200]);

    useEffect(() => {
        setRequestData(prev => ({
            ...prev,
            getPage: page.toString(),
        }));
    }, [page]);

    const handleSearch = ({ nextDept = dept, nextCode = code }: { nextDept?: string; nextCode?: string } = {}) => {
        const newRequestData: BillListRequest = {
            getPage: "1",
            getPageSize: "20",
            flag: flag,
            yyyy: flag === "month" ? year : undefined,
            mm: flag === "month" ? month : undefined,
            startDate: flag === "day" && startDate ? format(startDate, "yyyy-MM-dd") : "",
            endDate: flag === "day" && endDate ? format(endDate, "yyyy-MM-dd") : "",
            s_p_dept: nextDept === "all" ? "" : nextDept,
            et_code: nextCode === "all" ? "" : nextCode,
        };

        setRequestData(newRequestData);
    };

    const handleDownload = () => {
        const data = {
            flag: flag,
            yyyy: year,
            mm: month,
            // startDate: startDate?.toISOString().split("T")[0] ?? "",
            // endDate: endDate?.toISOString().split("T")[0] ?? "",
            startDate: startDate ? format(startDate, "yyyy-MM-dd") : "",
            endDate: endDate ? format(endDate, "yyyy-MM-dd") : "",
            s_p_dept: dept === "all" ? "" : dept,
            et_code: code === "all" ? "" : code,
        };

        getExcel(data);
    };

    const handlePrint = async () => {
        const initialParams = {
            getPage: 1,
            getPageSize: "2000",
            flag: flag,
            yyyy: year,
            mm: month,
            startDate: startDate ? format(startDate, "yyyy-MM-dd") : "",
            endDate: endDate ? format(endDate, "yyyy-MM-dd") : "",
            s_p_dept: dept === "all" ? "" : dept,
            et_code: code === "all" ? "" : code,
        };

        try {
            const response = await axiosInstance.post(enum_api_uri.bill_list, initialParams);
            const data = response.data.data;
            hasPrintedRef.current = false; // 프린트 초기화
            setPrintData(data);
        } catch (error: any) {
            const message = error.message;
            toast.error(message, toastErrorSyle);
        } finally {
        }
    };

    const handleOpenTrackingPop = ({ bl_no, count }: { bl_no: string; count: number }) => {
        const startWithA = bl_no.startsWith("A");
        const startWithE = bl_no.startsWith("E");

        if (startWithA) {
            const blNumber = bl_no.split("A")[1];
            postScanImg(
                {
                    s_bl_no: blNumber,
                },
                {
                    onSuccess: data => {
                        if (data?.data?.url && data?.data?.timestamp) {
                            setScanImg({
                                url: data.data.url,
                                timestamp: data.data.timestamp,
                            });
                        }
                    },
                    onError: () => {
                        setScanImg(null);
                        toast.error("송장 조회 실패", toastErrorSyle);
                    },
                },
            );
        } else if (startWithE) {
            const blNumber = bl_no.split("E")[1];
            postTrackingPopPrintView(
                {
                    chk_bl_no: blNumber,
                },
                {
                    onSuccess: data => {
                        setPrintA4Data(data);
                        setPrintA4Pop(true);
                    },
                    onError: () => {
                        toast.error("데이터 가져오기 실패", toastErrorSyle);
                    },
                },
            );

            return;
            // E 다음에 - 이후 숫자 추가 후 카운트만큼 bl_no-001, bl_no-002, bl_no-003, ...
            // const blNumber = "E" + "-" + bl_no.split("E")[1];
            const chk_bl_no = Array.from({ length: count }, (_, index) => `${blNumber}-${String(index + 1).padStart(3, "0")}`);
            // console.log(chk_bl_no);
            postTrackingPopPrint(
                {
                    print_number: 1,
                    chk_bl_no,
                    flag: "invoice",
                },
                {
                    onSuccess: data => {
                        setPrintA4Data(data);
                        setPrintA4Pop(true);
                    },
                    onError: () => {
                        toast.error("데이터 가져오기 실패", toastErrorSyle);
                    },
                },
            );
        }
    };

    const Bill = ({ billData, print = false }: { billData?: BillListResponse; print?: boolean }) => {
        return (
            <div className="bg-white w-full max-w-[1200px] px-[50px] pt-[65px] pb-10 mb-20 rounded-2xl flex flex-col responsive1200:px-[4%] responsive1200:pt-[30px]">
                <div className="w-full justify-between items-start flex responsive1200:flex-col">
                    <div className="flex flex-col responsive1200:w-full">
                        <p className="text-[#1B1C1E] text-2xl font-[500] border-b-2 border-[#1B1C1E] w-fit pb-1.5 responsive1200:text-[20px]">
                            {print ? (
                                <>
                                    {flag === "month"
                                        ? `${billData?.yyyy}년 ${billData?.mm}월분 청구내역서`
                                        : `${billData?.startDate.replaceAll("-", ".")} ~ ${billData?.endDate.replaceAll("-", ".")}`}
                                </>
                            ) : (
                                <>
                                    {billData?.yyyy
                                        ? `${billData?.yyyy}년 ${billData?.mm}월분 청구내역서`
                                        : `${billData?.startDate.replaceAll("-", ".")} ~ ${billData?.endDate.replaceAll("-", ".")}`}
                                </>
                            )}
                        </p>
                        <p className="pt-5 text-[#666] text-[18px] font-[500]">
                            업체 : [ {c_code} ] [ {c_company} ]
                        </p>
                        <div className="pt-[15px] flex items-center text-[#666] text-[18px] font-[500] gap-3">
                            <span className="text-nowrap">부서 :</span>
                            <SelectBox
                                value={dept}
                                onChange={e => {
                                    setDept(e);
                                    handleSearch({ nextDept: e });
                                }}
                                height="h-[48px]"
                                label="text-[#5A5C63] text-[16px]"
                                className="w-44 bg-white mr-1 responsive1200:w-full"
                                options={[
                                    { value: "all", label: "전체 부서" },
                                    ...(deptData?.map(dept => ({
                                        value: dept.sn.toString(),
                                        label: dept.dept_name,
                                    })) || []),
                                ]}
                            />
                            <span className="text-nowrap">운임 :</span>
                            <SelectBox
                                height="h-[48px] "
                                label="text-[#5A5C63] text-[16px]"
                                value={code}
                                onChange={e => {
                                    setCode(e);
                                    handleSearch({ nextCode: e });
                                }}
                                className="w-44 bg-white responsive1200:w-full"
                                options={[
                                    { value: "all", label: "전체 운임" },
                                    { value: "3", label: "신용(현불)" },
                                    { value: "4", label: "신용(착불)" },
                                    { value: "5", label: "제3자(신용)" },
                                ]}
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between border-[4px] border-[#dededd] py-[15px] pl-[18px] gap-7 w-full max-w-[484px] responsive1200:max-w-full responsive1200:mt-[20px]">
                        <div className="flex flex-col w-[308px] text-[15px]">
                            <div className="pb-2.5 text-[#333] font-[500]">
                                {billData?.companyInfo?.shop_name}
                                <br />
                                {billData?.companyInfo?.saup_no}
                            </div>
                            <div className="pt-2.5 border-t text-[#666]">
                                {billData?.companyInfo?.addr}
                                <br />
                                {billData?.companyInfo?.tel_no}
                            </div>
                        </div>
                        <div className="w-[106px] h-[104px] border flex items-center justify-center mr-4">
                            <img className="w-[50px]" src={Seal} alt="seal" />
                        </div>
                    </div>
                </div>

                <figure className="overflow-x-auto">
                    <table className="mt-12 text-sm min-w-[800px] w-full">
                        <thead className="border-t-2 border-black h-12 bg-[#f8f8f8]">
                            <tr className="text-[#333] border-b">
                                <th className="font-[400] border-r w-[88px]">일자</th>
                                <th className="font-[400] border-r text-nowrap">운송장번호</th>
                                <th className="font-[400] border-r w-[165px]">출발지</th>
                                <th className="font-[400] border-r w-[165px]">도착지</th>
                                <th className="font-[400] border-r w-[90px]">운임구분</th>
                                <th className="font-[400] border-r w-[88px]">담당자</th>
                                <th className="font-[400] border-r w-[66px]">수량</th>
                                {c_month_yn === "Y" && <th className="font-[400] border-r w-[88px]">월정료(kg)</th>}
                                <th className="font-[400] border-r w-[88px]">금액</th>
                                <th className="font-[400] border-r w-[88px]">추가금액</th>
                                <th className="font-[400] w-[77px]">합계</th>
                                <th className="font-[400] w-[77px]">메모</th>
                            </tr>
                        </thead>
                        <tbody className="text-[#666]">
                            {billData && billData?.billList?.length > 0 ? (
                                <>
                                    {billData?.billList?.map((item, index) => (
                                        <tr key={index} className="text-center border-b">
                                            <td className="border-r p-[13px] ">{item.s_Date}</td>
                                            <td
                                                className={`border-r p-[13px] hover:underline cursor-pointer text-[#003e9c]`}
                                                onClick={() => handleOpenTrackingPop({ bl_no: item.송장번호, count: item.수량 })}
                                            >
                                                <div className="flex items-center justify-center gap-2">
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 20 20"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M11.25 5H4.375C3.33947 5 2.5 5.83947 2.5 6.875V15.625C2.5 16.6605 3.33947 17.5 4.375 17.5H13.125C14.1605 17.5 15 16.6605 15 15.625V8.75M6.25 13.75L17.5 2.5M17.5 2.5L13.125 2.5M17.5 2.5V6.875"
                                                            stroke="#003e9c"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        ></path>
                                                    </svg>
                                                    {item.송장번호}
                                                </div>
                                            </td>
                                            <td className="border-r p-[13px]">{item.출발지}</td>
                                            <td className="border-r p-[13px]">{item.도착지}</td>
                                            <td className="border-r p-[13px] whitespace-nowrap">{item.구분}</td>
                                            <td className="border-r p-[13px]">{item.담당자}</td>
                                            <td className="border-r p-[13px]">{item.수량}</td>
                                            {c_month_yn === "Y" && <td className="border-r p-[13px]">{item.월정료}</td>}
                                            <td className="border-r p-[13px]">{item.금액.toLocaleString()}</td>
                                            <td className="border-r p-[13px]">{item.추가금액.toLocaleString()}</td>
                                            <td>{item.합계.toLocaleString()}</td>
                                            <td>
                                                <div className="group flex items-center justify-center relative">
                                                    {item.메모?.trim() !== "" ? (
                                                        <MemoOn className="cursor-default" />
                                                    ) : (
                                                        <Memo className="cursor-default" />
                                                    )}

                                                    {item.메모?.trim() !== "" && (
                                                        <div className="hidden group-hover:block absolute top-full -translate-y-2 -translate-x-5 right-0 z-50 w-[400px] p-5 bg-white rounded-2xl shadow-[0_0_16px_rgba(0,0,0,0.1)]">
                                                            <pre className="text-[16px] text-[#1B1C1E] font-[500] whitespace-pre-wrap break-keep">
                                                                {item.메모}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            ) : (
                                <tr className="text-center border-b text-[#333]">
                                    <td colSpan={11} className="p-10">
                                        <div className="flex flex-col items-center justify-center gap-3">
                                            <Notice />
                                            <p className="leading-[40px] w-full text-center text-[#878a93] text-[22px] font-semibold break-keep">
                                                {flag === "month" ? (
                                                    <>
                                                        월 청구내역서는 마감이후 조회 가능 합니다
                                                        <br /> 고객센터: 1566-0685
                                                    </>
                                                ) : (
                                                    "조회된 데이터가 없습니다."
                                                )}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </figure>

                {!print && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                        <button onClick={() => setPage(1)}>
                            <PageFirst />
                        </button>
                        <button onClick={() => setPage(prev => Math.max(1, startPage - pageSize))} disabled={startPage === 1}>
                            <PageBefore className="mr-7" />
                        </button>

                        {Array.from({ length: endPage - startPage + 1 }).map((_, i) => {
                            const pageNum = startPage + i;
                            return (
                                <button
                                    key={pageNum}
                                    className={`px-3 py-1 rounded ${pageNum === page ? "bg-[#1B1C1E] text-white" : "text-black"}`}
                                    onClick={() => setPage(pageNum)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        <button onClick={() => setPage(prev => Math.min(maxPage, startPage + pageSize))} disabled={endPage >= maxPage}>
                            <PageBefore className="rotate-180 ml-7" />
                        </button>
                        <button onClick={() => setPage(maxPage)}>
                            <PageFirst className="rotate-180" />
                        </button>
                    </div>
                )}

                <figure className="overflow-x-auto">
                    <table className="mt-16 text-sm min-w-[800px] w-full">
                        <thead className="border-t-2 border-black h-12 bg-[#f8f8f8]">
                            <tr className="text-[#333] border-b">
                                <th className="font-[400] border-r w-[176px]">월정료</th>
                                <th className="font-[400] border-r w-[176px]">기타(추가/할인)</th>
                                <th className="font-[400] border-r w-[176px]">공급가액</th>
                                <th className="font-[400] border-r w-[176px]">합계</th>
                                <th className="font-[400] border-r w-[176px]">부가세</th>
                                <th className="font-[400] text-nowrap">총합계</th>
                            </tr>
                        </thead>
                        <tbody className="text-[#666]">
                            {billData?.sumList && (
                                <tr className="text-center border-b text-[#333]">
                                    <td className="border-r p-[13px]">
                                        {c_month_yn === "Y" ? billData.sumList.월정료.toLocaleString() : 0}
                                    </td>
                                    <td className="border-r p-[13px]">{billData.sumList.추가할인.toLocaleString()}</td>
                                    <td className="border-r p-[13px]">{billData.sumList.공급가액.toLocaleString()}</td>
                                    <td className="border-r p-[13px]">{billData.sumList.합계.toLocaleString()}</td>
                                    <td className="border-r p-[13px]">{billData.sumList.부가세.toLocaleString()}</td>
                                    <td className="p-[13px]">{billData.sumList.총합계.toLocaleString()}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </figure>

                <div className="text-[15px] flex flex-col gap-3 mt-6">
                    <p className="text-[#e61d27]">※ 입금 확인을 위하여 입금시 반드시 '상호명'으로 입금 바랍니다.</p>
                    <div className="text-[#666]">
                        <p>※ 입금계좌</p>
                        <p className="ml-[18px]">예금주 : {billData?.companyInfo?.bank_info}</p>
                        <p className="ml-[18px]">{billData?.companyInfo?.bank_info2}</p>
                    </div>
                    <p className="text-[#666]">※ http://www.ilshingroup.com</p>
                </div>
            </div>
        );
    };

    const BillPrint = ({ data, onClose }: { data: BillListResponse; onClose: () => void }) => {
        useEffect(() => {
            if (!data.billList || hasPrintedRef.current) return;

            hasPrintedRef.current = true;

            const handleAfterPrint = () => {
                setTimeout(() => {
                    onClose();
                }, 100);
            };

            window.addEventListener("afterprint", handleAfterPrint, { once: true });

            setTimeout(() => {
                window.print();
            }, 200);

            return () => {
                window.removeEventListener("afterprint", handleAfterPrint);
            };
        }, [data]);

        return ReactDOM.createPortal(
            <>
                {/* size: A4 portrait; */}
                {/* size: landscape; */}
                <style>
                    {`
                    @media print {
                    @page {
                        margin: 10mm 0 0;
     
                    }

                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100% !important;
                        height: 100% !important;

                        max-width: none;
                        overflow: visible;
                    }

                    #print-bill {
                        width: 100%;
                        padding: 0;
                        margin: 0;
                    }

                    #print-bill * {
                        visibility: visible !important;
                    }
                    }
                `}
                </style>
                <div id="print-bill" className="hidden print:block min-w-[1200px]">
                    <Bill billData={data} print />
                </div>
            </>,
            document.body,
        );
    };

    const ScanImgPopup = ({ scanImg, onClose }: { scanImg: { url: string; timestamp: number }; onClose: () => void }) => {
        useEffect(() => {
            if (!scanImg?.timestamp) return;

            // 이미지 생성 시간부터 5분 후를 계산
            const imageCreatedTime = scanImg.timestamp;
            const expiryTime = imageCreatedTime + 5 * 60 * 1000; // 5분 후
            const currentTime = Date.now();

            // 이미 만료된 경우 즉시 닫기
            if (currentTime >= expiryTime) {
                onClose();
                return;
            }

            // 남은 시간만큼 타이머 설정
            const remainingTime = expiryTime - currentTime;
            const timer = setTimeout(() => {
                onClose();
            }, remainingTime);

            // cleanup 함수에서 타이머 정리
            return () => {
                clearTimeout(timer);
            };
        }, [scanImg, onClose]);

        const handleImageError = () => {
            // 이미지 로드 실패 시 (URL 만료 등) 팝업 닫기
            onClose();
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                <div className="bg-white p-[50px] rounded-2xl flex flex-col gap-5 responsive1200:px-[4%] responsive1200:pt-[30px] relative">
                    <div className="flex justify-end items-center gap-3">
                        <Close className="cursor-pointer" onClick={() => onClose()} />
                    </div>
                    <a href={scanImg.url} target="_blank" rel="noopener noreferrer" title="새창으로 열기">
                        <img src={scanImg.url} alt="scan-img" onError={handleImageError} className="max-h-[600px]" />
                    </a>
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <Seo type="morning" pageTitle="청구서조회" />
            {(isPostScanImgPending || isPostTrackingPopPrintPending) && <Spinner />}
            <CustomerLayout>
                <div className="flex-1 bg-[#f8f8f8] flex flex-col items-center pt-12 responsive1200:px-[4%] responsive1200:pt-[30px]">
                    <p className="text-[#1b1c1e] text-[36px] font-bold  responsive1200:text-[24px] responsive1200:font-[500]">청구서조회</p>
                    {accessToken ? (
                        <>
                            <div className="bg-white mt-12 w-full max-w-[1200px] px-10 py-6 rounded-2xl flex flex-col mb-6 responsive1200:p-[24px_4%] responsive1200:mt-[30px]">
                                <div className="flex gap-32 w-full items-center justify-end responsive1200:flex-col responsive1200:gap-[8px]">
                                    <p className="text-[18px] font-[500] responsive1200:self-start">미납금액 (당월제외)</p>
                                    <div className="flex gap-6 responsive1200:w-full">
                                        <p className="text-new font-bold text-[28px] flex-1 max-w-[240px] text-end responsive1200:ml-auto">
                                            0원
                                        </p>
                                        <Button>미납상세</Button>
                                    </div>
                                </div>
                                <div className="bg-[#f7f7f8] text-[#5A5C63] py-4 flex items-center justify-center gap-8 mt-3 border-t responsive1200:bg-white responsive1200:flex-col responsive1200:gap-0">
                                    {/* 1200 이하/이상 */}
                                    {responsive1200 ? (
                                        <p className="w-full text-[14px] text-[#333333] mb-[5px]">월별</p>
                                    ) : (
                                        <SelectBox
                                            height="h-[50px]"
                                            label="text-[#5A5C63] text-[16px]"
                                            value={flag}
                                            onChange={e => {
                                                setFlag(e);
                                            }}
                                            className="w-[118px] bg-[#FEE2E2]"
                                            options={[
                                                { value: "month", label: "월별" },
                                                { value: "day", label: "일자별" },
                                            ]}
                                        />
                                    )}

                                    {flag === "month" ? (
                                        <div className="flex items-center justify-center gap-8 responsive1200:w-full">
                                            <SelectBox
                                                height="h-[50px]"
                                                label="text-[#5A5C63] text-[16px]"
                                                value={year}
                                                onChange={e => {
                                                    setYear(e);
                                                }}
                                                className="min-w-[118px] bg-white responsive1200:w-full"
                                                options={[
                                                    { value: "2026", label: "2026" },
                                                    { value: "2025", label: "2025" },
                                                    { value: "2024", label: "2024" },
                                                    { value: "2023", label: "2023" },
                                                    { value: "2022", label: "2022" },
                                                    { value: "2021", label: "2021" },
                                                    { value: "2020", label: "2020" },
                                                ]}
                                            />
                                            <p className="-ml-5">년</p>
                                            <SelectBox
                                                height="h-[50px]"
                                                label="text-[#5A5C63] text-[16px]"
                                                value={month}
                                                onChange={e => {
                                                    setMonth(e);
                                                }}
                                                className="min-w-[118px] bg-white responsive1200:w-full"
                                                options={[
                                                    { value: "01", label: "01" },
                                                    { value: "02", label: "02" },
                                                    { value: "03", label: "03" },
                                                    { value: "04", label: "04" },
                                                    { value: "05", label: "05" },
                                                    { value: "06", label: "06" },
                                                    { value: "07", label: "07" },
                                                    { value: "08", label: "08" },
                                                    { value: "09", label: "09" },
                                                    { value: "10", label: "10" },
                                                    { value: "11", label: "11" },
                                                    { value: "12", label: "12" },
                                                ]}
                                            />
                                            <p className="-ml-5">월</p>
                                        </div>
                                    ) : (
                                        <div className="flex gap-3 relative bg-white h-[52px] px-3 border border-[#E1E2E4] rounded-lg">
                                            <DayPickerInput initialDate={startDate} twoCalendar onDateChange={setStartDate} />
                                            <DayPickerInput initialDate={endDate} twoCalendar onDateChange={setEndDate} />
                                        </div>
                                    )}

                                    <div className="flex gap-2 ml-4 responsive1200:w-full responsive1200:ml-0 responsive1200:mt-[20px]">
                                        <Button
                                            onClick={() => handleSearch()}
                                            className="bg-[#132E47] border-none text-[15px] text-white !font-[400] w-[110px] min-h-12 responsive1200:flex-1"
                                        >
                                            조회하기
                                        </Button>
                                        <Button
                                            onClick={() => handleDownload()}
                                            className="bg-white border-black text-[15px] text-black !font-[400] w-[110px] min-h-12 responsive1200:flex-1"
                                        >
                                            엑셀다운
                                        </Button>
                                        <Button
                                            onClick={() => handlePrint()}
                                            className="bg-white border-black text-[15px] text-black !font-[400] w-[110px] min-h-12 responsive1200:flex-1"
                                        >
                                            인쇄
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <Bill billData={billData} />
                        </>
                    ) : (
                        <div className="mt-10 w-full h-[68vh]">
                            <NoLogin status="morning" />
                        </div>
                    )}
                </div>

                {printData && <BillPrint data={printData} onClose={() => setPrintData(null)} />}

                {scanImg && <ScanImgPopup scanImg={scanImg} onClose={() => setScanImg(null)} />}

                <>
                    {printA4Pop && (
                        <div className="fixed inset-0 z-[200] bg-black bg-opacity-50 flex items-center justify-center p-4">
                            {printA4Data && printA4Data?.length > 0 ? (
                                <div className="bg-white rounded-2xl flex flex-col max-h-[90vh] w-full max-w-[900px] shadow-2xl">
                                    <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                                        <p className="text-xl font-bold">운송장</p>
                                        <Close
                                            className="cursor-pointer hover:opacity-70 transition-opacity"
                                            onClick={() => {
                                                setPrintA4Pop(false);
                                                setPrintA4Data(null);
                                            }}
                                        />
                                    </div>
                                    <div className="overflow-auto p-6 flex-1">
                                        <PrintA4Preview
                                            data={printA4Data}
                                            onClose={() => {
                                                setPrintA4Pop(false);
                                                setPrintA4Data(null);
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl p-8 max-w-md">
                                    <div className="flex justify-end items-center mb-4">
                                        <Close className="cursor-pointer" onClick={() => setPrintA4Pop(false)} />
                                    </div>
                                    <p className="text-center text-gray-600">데이터가 없습니다.</p>
                                </div>
                            )}
                        </div>
                    )}
                </>

                <Toaster position="bottom-center" reverseOrder={false} />
            </CustomerLayout>
        </React.Fragment>
    );
};
export default MorningInvoices;
